import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { AuthShell, Field } from "./Login";
import { toast } from "sonner";
import { PARTNER } from "@/constants/testIds";
import { Building2, ArrowUpRight } from "lucide-react";

const KIND_OPTIONS = [
  ["employability_organisation", "Organisme d'employabilité"],
  ["chamber_of_commerce", "Chambre de commerce"],
  ["professional_association", "Association professionnelle"],
  ["incubator", "Incubateur / Accélérateur"],
  ["training_provider", "Établissement de formation"],
  ["public_agency", "Agence publique"],
  ["enterprise", "Entreprise"],
  ["other", "Autre"],
];

export default function PartnerRegister() {
  const { t, lang } = useI18n();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    org_name: "", org_kind: "employability_organisation", country: "", region: "", website: "",
    contact_name: "", contact_email: "", contact_password: "",
  });
  const [busy, setBusy] = useState(false);

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
    className: "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/partner/register", form);
      setToken(data.access_token);
      await refreshUser();
      toast.success(lang === "fr" ? "Organisation enregistrée · en attente d'approbation" : "Organisation registered · pending approval");
      navigate("/app/partner", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#0B2545]">Career OS · Partner Onboarding</div>
      <h1 className="mt-4 font-display text-3xl tracking-tight text-[#1F2937]">
        {lang === "fr" ? "Enregistrer votre organisme" : "Register your organisation"}
      </h1>
      <p className="mt-3 text-sm text-[#5B5B62]">
        {lang === "fr"
          ? "Rejoignez ALTIORA Career OS en tant qu'organisme partenaire pour suivre vos bénéficiaires dans un espace isolé."
          : "Join ALTIORA Career OS as a partner to track your beneficiaries in an isolated workspace."}
      </p>

      <form onSubmit={submit} className="mt-6 space-y-3">
        <Field label={lang === "fr" ? "Nom de l'organisme" : "Organisation name"}>
          <input data-testid={PARTNER.orgName} required {...bind("org_name")} />
        </Field>
        <Field label={lang === "fr" ? "Type" : "Kind"}>
          <select data-testid={PARTNER.orgKind} {...bind("org_kind")}>
            {KIND_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={lang === "fr" ? "Pays" : "Country"}>
            <input data-testid={PARTNER.country} placeholder="CA" {...bind("country")} />
          </Field>
          <Field label={lang === "fr" ? "Région / Province" : "Region"}>
            <input data-testid={PARTNER.region} placeholder="QC" {...bind("region")} />
          </Field>
        </div>
        <Field label={lang === "fr" ? "Site web" : "Website"}>
          <input data-testid={PARTNER.website} type="url" placeholder="https://" {...bind("website")} />
        </Field>
        <div className="pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#0B2545]">
          {lang === "fr" ? "Contact administrateur" : "Admin contact"}
        </div>
        <Field label={lang === "fr" ? "Nom complet" : "Full name"}>
          <input data-testid={PARTNER.contactName} required {...bind("contact_name")} />
        </Field>
        <Field label={lang === "fr" ? "Courriel" : "Email"}>
          <input data-testid={PARTNER.contactEmail} type="email" required {...bind("contact_email")} />
        </Field>
        <Field label={lang === "fr" ? "Mot de passe" : "Password"}>
          <input data-testid={PARTNER.contactPassword} type="password" minLength={8} required {...bind("contact_password")} />
        </Field>
        <button
          data-testid={PARTNER.submit}
          type="submit" disabled={busy}
          className="w-full mt-4 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-60 text-white font-medium py-2.5 transition-colors flex items-center justify-center gap-2"
        >
          <Building2 className="h-4 w-4" strokeWidth={2} />
          {busy ? t("common.loading") : (lang === "fr" ? "Enregistrer l'organisme" : "Register organisation")}
        </button>
      </form>

      <p className="mt-6 text-xs text-[#5B5B62] text-center">
        {lang === "fr" ? "Vous êtes un particulier ?" : "Individual user?"}{" "}
        <Link to="/register" className="text-[#0B2545] hover:text-[#DCC29A] inline-flex items-center gap-0.5">
          {lang === "fr" ? "Créer un compte" : "Sign up"} <ArrowUpRight className="h-3 w-3" />
        </Link>
      </p>
    </AuthShell>
  );
}
